import moment from "moment-timezone";
import Form from "rc-field-form";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import Button from "../../../../../../../components/Button";
import Field from "../../../../../../../components/Field";
import Text from "../../../../../../../components/Inputs/Text";
import Modal from "../../../../../../../components/Modal";

const getSuffix = (number) => {
    const suffix = ["th", "st", "nd", "rd"];
    const value = number % 100;
    return suffix[(value - 20) % 10] || suffix[value] || suffix[0];
}

const WaiveModal = ({
    isOpen,
    onClose,
    onConfirm,
    canWaive,
    mealPremiumRule,
    isLoadingMealPremiumRule,
    isMyTimesheet,
    mealPremiums,
    isWaivedByManager
}) => {
    const { t } = useTranslation();

    const content = useMemo(() => {
        if (isLoadingMealPremiumRule) {
            return (
                <p>{t("loading-meal-premium-rule")}</p>
            );
        }

        if (!mealPremiumRule) {
            return (
                <p>{t("could-not-find-meal-premium-rule")}</p>
            );
        }

        if (isMyTimesheet) {
            return (
                <p>
                    {t("i-agree-to-waive-my-meal-periods-as-follows")}:

                    <ul>
                        {mealPremiumRule.rules.map((rule, i) => {
                            const nextRule = mealPremiumRule.rules[i + 1];
                            const nextRuelDuration = nextRule
                                ? moment.duration(nextRule.duration, 'm').subtract(1, 'm').asHours().toFixed(2)
                                : null;
                            return (
                                <li key={i.toString()}>
                                    <div>
                                        {nextRuelDuration
                                            ? t("meal-premium-agreement-1-2", {
                                                min: rule.amount,
                                                max: moment.duration(rule.duration, 'm').subtract(1, 'm').asHours().toFixed(2),
                                                amount: nextRuelDuration
                                            })
                                            : t("meal-premium-agreement-1-1", {
                                                min: rule.amount,
                                                max: moment.duration(rule.duration, 'm').subtract(1, 'm').asHours().toFixed(2),
                                            })}
                                    </div>
                                    <div>
                                        {nextRuelDuration ? t("meal-premium-agreement-2-2", { amount: nextRuelDuration }) : t("meal-premium-agreement-2-1")}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    {mealPremiums.length === 1 && (
                        t("meal-premium-agreement-3-1")
                    )}
                    {mealPremiums.length === 2 && (
                        t("meal-premium-agreement-3-2")
                    )}
                    {mealPremiums.length > 2 && (
                        t("meal-premium-agreement-3-3", {
                            waive: `${mealPremiums.length}${getSuffix(mealPremiums.length)}`,
                            take: `${mealPremiums.slice(2).map((_, i) => `${i + 1}${getSuffix(i + 1)}`).join(', ')} and ${mealPremiums.length - 1}${getSuffix(mealPremiums.length - 1)}`,
                        })
                    )}
                    {" "}
                    {t("meal-premium-agreement-4")}
                </p>
            )
        } else {
            return (
                <p>
                    {t("manager-meal-premium-agreement-1")}
                    <br />
                    {t("manager-meal-premium-agreement-2")}
                </p>
            );
        }
    }, [t, isMyTimesheet, isLoadingMealPremiumRule, mealPremiumRule, mealPremiums]);

    const onFinish = useCallback((values) => {
        onConfirm({ note: values.note || '' });
    }, [onConfirm]);

    return (
        <Modal
            size="lg"
            title={
                <div className="d-flex justify-content-between align-items-center text-primary">
                    {isWaivedByManager ? t("meal-break-waiver-cancellation-agreement") : t("meal-break-waiver-agreement")}
                    <i
                        className="fa-solid fa-close cursor-pointer"
                        onClick={onClose}
                    />
                </div>
            }
            toggle={onClose}
            isOpen={isOpen}
            scrollable
            centered
        >
            <Form onFinish={onFinish}>
                {!isWaivedByManager && content}

                <Field name="note" label={t("note")}>
                    <Text type="textarea" rows={5} style={{ resize: "none" }} />
                </Field>

                <Row>
                    <Col md="6">
                        <Button color="danger" className="w-100" onClick={onClose}>
                            {t("close")}
                        </Button>
                    </Col>

                    <Col md="6">
                        <Button type="submit" color="primary" className="w-100" disabled={!canWaive}>
                            {isWaivedByManager ? t("cancel-meal-waive") : t("confirm")}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
}

export default WaiveModal;
